import {useCallback, useEffect, useRef, useState} from 'react';

import {debounce} from 'lodash';

import {getLocalizationService} from '~/shared/services/localisationService';
import {createLogger} from '~/shared/logging';
import {trackEvent} from '~/shared/services/analytics';
import {GeolocationPlace, getCurrentGeolocationPlace} from '~/shared/services/geocoder';
import {Address, LocalAddress} from '~/shared/store/models';
import {AddressPartsFromKey} from '~/shared/utils/address';

import getAddressFinderAddress from './getAddressFinderAddress';

const logger = createLogger('useAddressFinder');

const useAddressFinder = ({setDisableSubmit, onChange, location, submitForm, disableAutoFocus, disableSubmit}: {
  location: string;
  submitForm: (address?: Address | LocalAddress | AddressPartsFromKey) => void;
  onChange: (address?: Address | LocalAddress | AddressPartsFromKey) => void;
  setDisableSubmit: React.Dispatch<boolean>;
  disableAutoFocus: boolean;
  disableSubmit: boolean;
}) => {
  const {t} = getLocalizationService();
  const [currentPlace, setCurrentPlace] = useState<GeolocationPlace | null>(null);
  const [customHouseNumber, setCustomHouseNumber] = useState();
  const [showAddressNumberInput, setShowAddressNumberInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const addressSelectorRef = useCallback((domEl: HTMLInputElement) => {
    if (domEl && !disableAutoFocus) {
      domEl.focus();
    }
  }, [disableAutoFocus]);

  const debouncedSetCustomHouseNumber = debounce(value => {
    setCustomHouseNumber(value);
  }, 300);

  const onPlaceSelected = useCallback(
    async (place: GeolocationPlace) => {
      if (!place || !place.place_id) {
        setErrorMessage(t('unexpected_error'));
        return;
      }

      setCurrentPlace(place);

      const addressRes = await getAddressFinderAddress(customHouseNumber, place);
      if ('error' in addressRes) {
        const {error: addressError} = addressRes;
        switch (addressError.type) {
          case 'missing':
            setErrorMessage(t('missing_xxx_please_fill_xxx', {field: addressError.field ? t(addressError.field) : ''}));
            break;
          case 'unexpected':
            setErrorMessage(t('unexpected_error'));
            break;
          case 'missing_houseNumber':
            setDisableSubmit(true);
            setShowAddressNumberInput(true);
            setErrorMessage(t('missing_xxx_please_fill_xxx', {field: addressError.field ? t(addressError.field) : ''}));
            break;
          default:
            logger.warn('unhandled error');
        }

        return;
      }

      const {data: address} = addressRes;
      setErrorMessage('');
      setDisableSubmit(false);

      onChange(address);

      if (location) {
        trackEvent('hasModifiedAddress', {
          modifiedAddressType: 'add address',
          modifiedLocation: location,
        });
      }

      submitForm(address);
    },
    [customHouseNumber, setDisableSubmit, onChange, location, t, submitForm],
  );

  const resetAddress = useCallback(() => {
    setDisableSubmit(true);
    setErrorMessage('');
    setShowAddressNumberInput(false);
    setCurrentPlace(null);
    onChange();
  }, [onChange, setDisableSubmit]);

  const getCurrentLocation = useCallback(async ({setAddress}: {setAddress: (address: string) => void}) => {
    try {
      const place = await getCurrentGeolocationPlace();

      resetAddress();
      setAddress(place.name);
      onPlaceSelected(place);

      trackEvent('addressStartSearch', {
        searchOption: 'location_based',
      });
    } catch (err) {
      // ignore- failing getting the initial device address will just leave the address input empty.
      logger.warn('Cant get initial location', err);
    }
  }, [onPlaceSelected, resetAddress]);

  const handleCustomHouseNumberChange = useCallback((value: string) => {
    debouncedSetCustomHouseNumber(value);
  }, [debouncedSetCustomHouseNumber]);

  const removeValidationError = useCallback(() => {
    setErrorMessage('');
  }, []);

  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    trackEvent('formErrorMessageView', {
      errorType: errorMessage,
      formName: 'address',
    });
  }, [errorMessage]);

  useEffect(() => {
    const keyEventsHandler = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !disableSubmit) {
        submitForm();
      }
    };
    rootRef.current?.addEventListener('keydown', keyEventsHandler, false);

    const refForCleanup = rootRef.current;
    return () => refForCleanup?.removeEventListener('keydown', keyEventsHandler, false);
  }, [addressSelectorRef, disableSubmit, rootRef, submitForm]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      if (currentPlace) {
        onPlaceSelected(currentPlace);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customHouseNumber]);

  return {
    errorMessage,
    showAddressNumberInput,
    rootRef,
    onPlaceSelected,
    resetAddress,
    getCurrentLocation,
    handleCustomHouseNumberChange,
    addressSelectorRef,
    removeValidationError,
  };
};

export default useAddressFinder;
